import { IconCalendar } from '@tabler/icons-react';
import { EnrollmentStatus } from '@zorro/clients';
import {
  formatDateEnLocaleMonthDay,
  getDaysLeft,
  isDateOnOrBeforeToday,
  parseDateISO,
} from '@zorro/shared/formatters';
import { FullPageLoader } from '@zorro/web/shared-ui';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { $enum } from 'ts-enum-util';

import { EnrollmentAction } from '../../../EmployeePageHeader';
import { EmployeePagePlansCard } from './EmployeePagePlansCard';
import { EmployeePagePlansElectionActive } from './EmployeePagePlansElectionActive';
import { EmployeePagePlansElectionSubmitted } from './EmployeePagePlansElectionSubmitted';
import { formatElectionWindowSubtitle } from './EmployeePagePlansUtils';

type Props = {
  onEnrollmentTabActionChange: (action?: EnrollmentAction) => void;
  onboardingPeriodId: string;
  employerId: string;
  employeeId: string;
  isDon: boolean;
};

export const EmployeePagePlans = ({
  onEnrollmentTabActionChange,
  onboardingPeriodId,
  employerId,
  employeeId,
  isDon,
}: Props) => {
  const { data: onboardingPeriod, isFetching } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: [onboardingPeriodId],
  });

  if (!onboardingPeriod || isFetching) {
    return <FullPageLoader />;
  }

  const { onboardingFrom, onboardingUntil, effectiveFrom, effectiveUntil } =
    onboardingPeriod;

  const waiverTitle = `Waiver effective period ${formatDateEnLocaleMonthDay(effectiveFrom)} - ${formatDateEnLocaleMonthDay(effectiveUntil)}`;
  const waiveSubtitle = formatElectionWindowSubtitle(onboardingPeriod);
  const effectiveYear = parseDateISO(effectiveFrom).year();
  const daysLeftOnboardingUntil = getDaysLeft(onboardingUntil);
  const isElectionWindowOpen = isDateOnOrBeforeToday(onboardingFrom);
  const pendingElectionWindowTitle = isElectionWindowOpen
    ? `Window open but on hold behind another election. Closes in ${daysLeftOnboardingUntil} days`
    : `Election window for plan year ${effectiveYear} starts in ${getDaysLeft(onboardingFrom)} days`;

  return $enum.mapValue(onboardingPeriod.enrollmentStatus).with({
    [EnrollmentStatus.PENDING_ELECTION_WINDOW]: (
      <EmployeePagePlansElectionActive
        title={pendingElectionWindowTitle}
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
        onboardingPeriodId={onboardingPeriodId}
        employeeId={employeeId}
        withActiveImpersonationButton
      />
    ),
    [EnrollmentStatus.ELECTION_ACTIVE_HAS_NOT_STARTED]: (
      <EmployeePagePlansElectionActive
        title={`No plans selected. ${daysLeftOnboardingUntil} days left until the election window ends`}
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
        onboardingPeriodId={onboardingPeriodId}
        employeeId={employeeId}
        withActiveImpersonationButton
        withSendReminderButton
      />
    ),
    [EnrollmentStatus.ELECTION_ACTIVE_STARTED]: (
      <EmployeePagePlansElectionActive
        title={`Plan selection started without submission, window ends in ${daysLeftOnboardingUntil} days`}
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
        onboardingPeriodId={onboardingPeriodId}
        employeeId={employeeId}
        withActiveImpersonationButton
        withSendReminderButton
      />
    ),
    [EnrollmentStatus.DEADLINE_PASSED]: (
      <EmployeePagePlansElectionActive
        title={`Election window ended ${Math.abs(daysLeftOnboardingUntil)} days ago`}
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
        onboardingPeriodId={onboardingPeriodId}
        employeeId={employeeId}
      />
    ),
    [EnrollmentStatus.ELECTION_SUBMITTED]: (
      <EmployeePagePlansElectionSubmitted
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
        isDon={isDon}
      />
    ),
    [EnrollmentStatus.ENROLLMENT_IN_PROCESS]: (
      <EmployeePagePlansElectionSubmitted
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
        isDon={isDon}
      />
    ),
    [EnrollmentStatus.ENROLLMENT_CONFIRMED]: (
      <EmployeePagePlansElectionSubmitted
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
        isDon={isDon}
      />
    ),
    [EnrollmentStatus.ACTIVE_COVERAGE]: (
      <EmployeePagePlansElectionSubmitted
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
        isDon={isDon}
      />
    ),
    [EnrollmentStatus.COVERAGE_ENDED]: (
      <EmployeePagePlansElectionSubmitted
        onEnrollmentTabActionChange={onEnrollmentTabActionChange}
        onboardingPeriodId={onboardingPeriodId}
        employerId={employerId}
        employeeId={employeeId}
        isDon={isDon}
      />
    ),
    [EnrollmentStatus.WAIVED_COVERAGE]: (
      <EmployeePagePlansCard
        subtitle={waiveSubtitle}
        icon={<IconCalendar />}
        title={waiverTitle}
      />
    ),
    [EnrollmentStatus.WAIVED_ELECTION]: (
      <EmployeePagePlansCard
        subtitle={waiveSubtitle}
        icon={<IconCalendar />}
        title={waiverTitle}
      />
    ),
    [EnrollmentStatus.ELECTION_ACTIVE]: null, //  TODO: Edit this enum to remove this status as it does nothing and never gets returned by the backend https://linear.app/myzorro/issue/ENG-467/delete-election-active-from-enrollmentstatus-enum
    [EnrollmentStatus.NO_ENROLLMENTS]: null,
    [$enum.handleUnexpected]: null,
  });
};
